import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Icon } from '../ui/Icon.tsx';
import { useWorkspace } from '../state/workspace.tsx';
import { canStartViewAs, formatViewAsCountdown, viewAsCandidates } from '../state/viewAs.ts';

export function ViewAsBanner() {
  const ws = useWorkspace();
  const viewAs = ws.session.view_as;
  const [now, setNow] = useState(() => Date.now());
  const [exiting, setExiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!viewAs) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [viewAs?.expires_at, viewAs?.user_id]);

  if (!viewAs) return null;
  const countdown = formatViewAsCountdown(viewAs.expires_at, now);

  return (
    <div className="session-banner view-as-banner" role="region" aria-label="Viewing as another user">
      <span className="view-as-copy">
        <Icon icon={Eye} size="1em" /> You are viewing Sidetone as {viewAs.display_name}. Read-only.
      </span>
      <span className="view-as-countdown" aria-live="off">{countdown}</span>
      <button
        type="button"
        className="btn btn-small"
        disabled={exiting}
        onClick={() => {
          setExiting(true);
          setError(null);
          void (ws.stopViewAs ? ws.stopViewAs() : Promise.resolve(null)).then(err => {
            setExiting(false);
            if (err) setError(err.title);
          });
        }}
      >
        <Icon icon={EyeOff} size="1em" /> Exit
      </button>
      {error ? <span className="field-error" role="alert">{error}</span> : null}
    </div>
  );
}

export function ViewAsMenuSection({ onDone }: { onDone: () => void }) {
  const ws = useWorkspace();
  const candidates = viewAsCandidates(ws.session, ws.roster.value);
  const active = ws.session.view_as ?? null;
  if (!canStartViewAs(ws.session)) return null;
  if (candidates.length === 0 && !active) return null;
  return (
    <div className="menu-group" role="group" aria-label="View as">
      <p className="meta">View as</p>
      {active ? (
        <button
          type="button"
          className="menu-item"
          onClick={() => {
            onDone();
            void ws.stopViewAs?.();
          }}
        >
          <Icon icon={EyeOff} /> Exit view as {active.display_name}
        </button>
      ) : null}
      {candidates.slice(0, 12).map(u => (
        <button
          key={u.id}
          type="button"
          className={`menu-item${active?.user_id === u.id ? ' is-current' : ''}`}
          onClick={() => {
            onDone();
            void ws.startViewAs?.(u.id);
          }}
        >
          <Icon icon={Eye} /> View as {u.display_name}
        </button>
      ))}
    </div>
  );
}
